/**
 * Assistant Store
 *
 * Global state for the floating AI assistant panel. Lifting chat state
 * into a zustand store (rather than useState inside the useAssistant
 * hook) keeps messages alive across:
 *
 *   - Panel open/close — the closed branch of AgionAssistant returns a
 *     different JSX tree (just the floating button), which unmounts
 *     everything that read from useAssistant's useState and reset
 *     messages to [] on reopen.
 *
 *   - SPA route changes — a tool result can legitimately carry a
 *     navigateTo meta ("show the rules page"), and the user expects
 *     the chat history to still be there after the navigation so they
 *     can follow up with "and the disabled ones?".
 *
 * Session-level persist via sessionStorage so a tab refresh keeps
 * context, but a new tab starts fresh (chat is per-tab by design —
 * not cross-tab broadcast).
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { MCPResultMeta } from '@/components/assistant/mcp-client'

export interface AssistantMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number // epoch ms — JSON-serialisable across session persist
  meta?: MCPResultMeta
  isError?: boolean
}

export interface PendingConfirmation {
  toolName: string
  args: Record<string, unknown>
}

interface AssistantState {
  /** Panel open/close — persisted across route changes but not tab reload */
  isOpen: boolean
  /** Chat history, persisted for the tab session */
  messages: AssistantMessage[]
  /** A preview awaiting confirmation (write tools) */
  pendingConfirmation: PendingConfirmation | null

  open: () => void
  close: () => void
  toggle: () => void

  addMessage: (msg: Omit<AssistantMessage, 'id' | 'timestamp'>) => AssistantMessage
  setMessages: (msgs: AssistantMessage[]) => void
  clearMessages: () => void

  setPendingConfirmation: (p: PendingConfirmation | null) => void
}

/** Build a stable message with an id + current timestamp. Exposed via
 *  addMessage so callers don't need to think about id generation. */
function newMessage(m: Omit<AssistantMessage, 'id' | 'timestamp'>): AssistantMessage {
  return {
    ...m,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: Date.now(),
  }
}

export const useAssistantStore = create<AssistantState>()(
  persist(
    (set) => ({
      isOpen: false,
      messages: [],
      pendingConfirmation: null,

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),

      addMessage: (m) => {
        const message = newMessage(m)
        set((s) => ({ messages: [...s.messages, message] }))
        return message
      },
      setMessages: (messages) => set({ messages }),
      clearMessages: () => set({ messages: [], pendingConfirmation: null }),

      setPendingConfirmation: (p) => set({ pendingConfirmation: p }),
    }),
    {
      name: 'agion-assistant-chat',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist the conversation-shaped state. isOpen resets to
      // closed on full refresh so a user who left the panel open and
      // reloaded doesn't get a modal jumping in their face.
      partialize: (s) => ({
        messages: s.messages,
        pendingConfirmation: s.pendingConfirmation,
        // cast so TS is happy — the rest of the state is ignored
      }) as Pick<AssistantState, 'messages' | 'pendingConfirmation'>,
      version: 1,
    },
  ),
)
